// sidebar_menu_controller.js
// Drives a left menu paired with a Turbo frame: the help centre (/hjelp/, #help-content) and
// the account descriptions (/kontobeskrivelser/, #account-content) both use it. Every page in
// such a section renders the same full menu (all subcategories, always expanded, every entry
// listed) beside the frame. Clicking an entry swaps only that frame — the menu stays put and
// the URL advances to the entry's permalink — so this controller only has to keep the menu in
// sync with the current URL, run the mobile drawer, and apply the AS / ENK company-type filter.
//
// The company-type filter is help-centre only (the account menu renders no filter buttons and
// no data-selskapstype, so everything matches): it hides article links that don't apply to the
// chosen type, and a subcategory disappears when it has nothing left. The choice is remembered
// across pages via a cookie.
import { Controller } from '@hotwired/stimulus'

const COOKIE = 'help_selskapstype'
const VALID = ['all', 'as', 'enk']

export default class extends Controller {
  static targets = ['button', 'item', 'link', 'section', 'empty', 'frame', 'sidebar', 'backdrop', 'menuButton']

  connect() {
    this.type = this.readPref()
    // We manage scroll ourselves on every in-page navigation, so stop the browser from
    // restoring an old (deep) scroll position on back/forward and fighting us.
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual'
    }
    // A Turbo frame swap (clicking an article) and back/forward both change the URL without
    // re-rendering the menu, so re-sync the active highlight whenever that happens.
    this.onFrameLoad = (event) => {
      if (this.hasFrameTarget && event.target !== this.frameTarget) return
      this.apply()
      // Scroll only once the new content is actually in place. Doing it earlier (right after
      // the click) mispositions when the target is shorter than what was shown — e.g. going
      // "back" to the short category overview, where the page shrinks after the scroll and
      // leaves the reader stranded at the bottom.
      this.scrollToArticle()
    }
    // Back/forward: reload the frame for whatever URL the history restored. The scroll is
    // handled by turbo:frame-load above, once the restored content is in place.
    this.onPopState = () => {
      if (this.hasFrameTarget) this.frameTarget.src = window.location.pathname
      this.apply()
    }
    // Escape closes the mobile menu drawer; a resize up to desktop makes it irrelevant.
    this.onKeydown = (event) => {
      if (event.key === 'Escape') this.closeMenu()
    }
    this.onResize = () => {
      if (window.innerWidth >= 1024) this.closeMenu()
    }
    document.addEventListener('turbo:frame-load', this.onFrameLoad)
    window.addEventListener('popstate', this.onPopState)
    document.addEventListener('keydown', this.onKeydown)
    window.addEventListener('resize', this.onResize)
    this.apply()
  }

  disconnect() {
    document.removeEventListener('turbo:frame-load', this.onFrameLoad)
    window.removeEventListener('popstate', this.onPopState)
    document.removeEventListener('keydown', this.onKeydown)
    window.removeEventListener('resize', this.onResize)
    document.body.classList.remove('overflow-hidden')
  }

  // Mobile menu drawer (hidden behind a "Meny" button on narrow screens; on wide screens the
  // menu sits in the left column, which the layout makes sticky, and these controls are hidden).
  openMenu() {
    if (!this.hasSidebarTarget) return
    this.sidebarTarget.classList.remove('-translate-x-full')
    if (this.hasBackdropTarget) this.backdropTarget.classList.remove('hidden')
    if (this.hasMenuButtonTarget) this.menuButtonTarget.setAttribute('aria-expanded', 'true')
    document.body.classList.add('overflow-hidden')
  }

  closeMenu() {
    if (!this.hasSidebarTarget) return
    this.sidebarTarget.classList.add('-translate-x-full')
    if (this.hasBackdropTarget) this.backdropTarget.classList.add('hidden')
    if (this.hasMenuButtonTarget) this.menuButtonTarget.setAttribute('aria-expanded', 'false')
    document.body.classList.remove('overflow-hidden')
  }

  select(event) {
    const type = event.currentTarget.dataset.type
    if (!VALID.includes(type)) return
    this.type = type
    this.writePref(type)
    this.apply()
  }

  // Open an entry (or the section overview) by swapping only the frame target's Turbo frame
  // and advancing the URL — the menu stays put, no full reload. Turbo Drive is disabled site
  // wide, so we drive the frame ourselves rather than relying on link interception. The href
  // is the no-JS fallback (a plain navigation to the entry's own page, which has the menu).
  navigate(event) {
    const href = event.currentTarget.getAttribute('href')
    if (!href || !this.hasFrameTarget) return
    event.preventDefault()
    if (window.location.pathname === href) return
    window.history.pushState({}, '', href)
    this.frameTarget.src = href
    this.apply()
    // Picking an article closes the mobile menu drawer (no-op on desktop). The scroll happens
    // on turbo:frame-load, once the new content is in place.
    this.closeMenu()
  }

  scrollToArticle() {
    // Going "back" to the short category/series overview: scroll to the very top of the page.
    // Scrolling to the frame instead would land at the bottom, since the brief overview isn't
    // tall enough to bring the frame up to the top of the viewport. The overview marks its
    // content with [data-overview], so this works for any menu (help centre, account series …).
    const isOverview = this.hasFrameTarget && this.frameTarget.querySelector('[data-overview]') != null
    if (!isOverview && this.hasFrameTarget) {
      // Reading from the top of the page the card already starts in plain view, below the
      // category title — pulling it up under the menu there would move the page for nothing.
      if (this.frameStartVisible()) return
      // Otherwise put its card at the top of the reading area. On desktop the frame's
      // lg:scroll-mt-… stops it just below the sticky top menu, so the menu and the sidebar
      // never move; on mobile (no sticky header, no scroll-margin) it lands flush against the
      // top and fills the screen. Jump straight there, no smooth-scroll animation.
      this.frameTarget.scrollIntoView({ behavior: 'auto', block: 'start' })
    } else {
      window.scrollTo({ top: 0, behavior: 'auto' })
    }
  }

  // Can the reader already see where the article starts? The frame's own scroll-margin-top is
  // exactly the line a scroll would park it on — below the sticky menu on desktop, 0 on mobile —
  // so a top edge at or below that line, and still above the fold, is already in the reading area.
  frameStartVisible() {
    const top = this.frameTarget.getBoundingClientRect().top
    const readingAreaTop = parseFloat(getComputedStyle(this.frameTarget).scrollMarginTop) || 0
    return top >= readingAreaTop && top < window.innerHeight
  }

  companyMatch(el) {
    const t = (el.dataset.selskapstype || '').trim()
    return this.type === 'all' || t === '' || t === this.type
  }

  apply() {
    // Filter article links by company type and note which sections still have any.
    let anyVisible = false
    this.itemTargets.forEach((item) => {
      const visible = this.companyMatch(item)
      item.classList.toggle('hidden', !visible)
      if (visible) anyVisible = true
    })

    // Hide a subcategory in the menu once it has no articles left for this company type.
    this.sectionTargets.forEach((section) => {
      const hasVisible = this.itemTargets.some((item) => section.contains(item) && !item.classList.contains('hidden'))
      section.classList.toggle('hidden', !hasVisible)
    })

    // Highlight the link for whatever article the URL currently points at.
    const path = window.location.pathname
    this.linkTargets.forEach((link) => {
      const active = link.dataset.articleId === path
      link.classList.toggle('bg-surface', active)
      link.classList.toggle('shadow-sm', active)
      link.classList.toggle('text-pink-luca', active)
      link.classList.toggle('text-content-subtle', !active)
      if (active) {
        link.setAttribute('aria-current', 'true')
      } else {
        link.removeAttribute('aria-current')
      }
    })

    // Note when the company-type filter has emptied the whole category.
    if (this.hasEmptyTarget) {
      this.emptyTarget.classList.toggle('hidden', anyVisible)
    }

    // Active company-type button styling: pink pill for the selected option.
    this.buttonTargets.forEach((btn) => {
      const active = btn.dataset.type === this.type
      btn.setAttribute('aria-pressed', active ? 'true' : 'false')
      btn.classList.toggle('bg-pink-luca', active)
      btn.classList.toggle('text-content-on-brand', active)
      btn.classList.toggle('shadow-sm', active)
      btn.classList.toggle('text-content-muted', !active)
      btn.classList.toggle('hover:text-content-subtle', !active)
    })
  }

  readPref() {
    let val
    try {
      if (window.Cookies) val = window.Cookies.get(COOKIE)
      if (!val) val = window.localStorage.getItem(COOKIE)
    } catch (e) {
      /* storage unavailable */
    }
    return VALID.includes(val) ? val : 'all'
  }

  writePref(type) {
    try {
      if (window.Cookies) {
        window.Cookies.set(COOKIE, type, { expires: 365, path: '/' })
      } else {
        window.localStorage.setItem(COOKIE, type)
      }
    } catch (e) {
      /* storage unavailable */
    }
  }
}
